import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { components as Core, MARKETPLACES_ENUM, useTranslation } from '../../../../core';
import { PREDEFINED_SMTP_PROTOCOL_ADDRESSES } from '../../../constants';
import * as actions from '../../../actions';
import * as selectors from '../../../selectors';
import { css, colors, fonts, spacing, StyleSheet } from '../../../../../styles';
import { AdministrationActionCard } from './AdministrationActionCard';
import { DetailsSection } from './DetailsSection';

/**
 * Displays marketplace-specific migration operations.
 *
 * @param {object} props
 * @param {string} props.companyId
 * @param {object} props.integration
 */
export const IntegrationMigrationActions = ({ companyId, integration }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isMigratingToAmazonSP = useSelector(state => (
        selectors.isMigratingToAmazonSP(state, integration.id)
    ));
    const isMigratingToNativeOutlook = useSelector(state => (
        selectors.isMigratingToNativeOutlook(state, integration.id)
    ));
    const isMigratingToRestApiEbay = useSelector(state => (
        selectors.isMigratingToRestApiEbay(state, integration.id)
    ));
    const isMigratingToEbay = useSelector(state => (
        selectors.isMigratingToEbay(state, integration.id)
    ));
    const isEbayMarketplace = integration.marketplace === MARKETPLACES_ENUM.Ebay;
    const isEbayFamilyMarketplace = [
        MARKETPLACES_ENUM.Ebay,
        MARKETPLACES_ENUM.RestApiEbay
    ].includes(integration.marketplace);
    const isOutlookEmail =
        integration.marketplace === MARKETPLACES_ENUM.Email &&
        integration.protocolAddress === PREDEFINED_SMTP_PROTOCOL_ADDRESSES.outlook;
    const isAmazonMarketplace = integration.marketplace === MARKETPLACES_ENUM.Amazon;
    const isMigratingEbay = isMigratingToRestApiEbay || isMigratingToEbay;

    const ebayMigration = isEbayMarketplace
        ? {
            onConfirm: () => dispatch(
                actions.migrateToRestApiEbay(integration.id, companyId)
            ).catch(() => null),
            cardTitle: t('integrations.administrationMigrationTitles.ebayToRestApi'),
            title: t('integrations.migrateToRestApiEbayTitle'),
            description: t('integrations.migrateToRestApiEbayDescription'),
            label: t('integrations.migrateToRestApiEbay')
        }
        : {
            onConfirm: () => dispatch(
                actions.migrateToEbay(integration.id, companyId)
            ).catch(() => null),
            cardTitle: t('integrations.administrationMigrationTitles.restApiToEbay'),
            title: t('integrations.migrateToEbayTitle'),
            description: t('integrations.migrateToEbayDescription'),
            label: t('integrations.migrateToEbay')
        };

    if (!isAmazonMarketplace && !isOutlookEmail && !isEbayFamilyMarketplace) return null;

    return (
        <DetailsSection title={t('integrations.administrationSections.migrations')}>
            <div className={css(styles.actionsGrid)}>
                {isAmazonMarketplace ? (
                    <AdministrationActionCard
                        title={t('integrations.administrationMigrationTitles.amazonToAmazonSp')}
                        description={t('integrations.administrationDescriptions.migrateAmazonSp')}
                        action={(
                            <Core.PopConfirm
                                onConfirm={() => dispatch(
                                    actions.migrateToAmazonSP(integration.id, companyId)
                                )}>
                                <span>
                                    <Core.Button
                                        label={t('integrations.migrateToAmazonSP')}
                                        isLoading={isMigratingToAmazonSP}
                                    />
                                </span>
                            </Core.PopConfirm>
                        )}
                    />
                ) : null}
                {isOutlookEmail ? (
                    <AdministrationActionCard
                        title={t('integrations.administrationMigrationTitles.smtpToOutlook')}
                        description={t('integrations.administrationDescriptions.migrateNativeOutlook')}
                        action={(
                            <Core.PopConfirm
                                onConfirm={() => dispatch(
                                    actions.migrateToNativeOutlook(integration.id, companyId)
                                )}>
                                <span>
                                    <Core.Button
                                        label={t('integrations.migrateToNativeOutlook')}
                                        isLoading={isMigratingToNativeOutlook}
                                    />
                                </span>
                            </Core.PopConfirm>
                        )}
                    />
                ) : null}
                {isEbayFamilyMarketplace ? (
                    <AdministrationActionCard
                        title={ebayMigration.cardTitle}
                        description={ebayMigration.description}
                        action={(
                            <Core.PopConfirm
                                onConfirm={ebayMigration.onConfirm}
                                title={(
                                    <div className={css(styles.migrationConfirmContent)}>
                                        <p>{ebayMigration.title}</p>
                                        <p>{ebayMigration.description}</p>
                                    </div>
                                )}>
                                <span>
                                    <Core.Button
                                        label={ebayMigration.label}
                                        isLoading={isMigratingEbay}
                                        isDisabled={isMigratingEbay}
                                    />
                                </span>
                            </Core.PopConfirm>
                        )}
                    />
                ) : null}
            </div>
        </DetailsSection>
    );
};

const styles = StyleSheet.create({
    actionsGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: spacing.s1
    },
    migrationConfirmContent: {
        ...fonts.smNormal,
        color: colors.black,
        '& p': {
            marginBottom: spacing.s3
        }
    }
});
